import React from 'react';
import { CircleSlash, EyeOff } from 'lucide-react';
import { Alert, Button, TextInput } from '@parsina/framework';
import { useMyPageStrings } from '../myPageStrings';
import { useDraft } from '../useDraft';
import { LIMITS, errorsBySection, saveStatus, sectionMeta, splitBookingFieldErrors } from '../MyPage.shared';
import { ErrorLine, PublishErrorList, SaveRow, useFieldStyle } from '../parts/common';
import HeroEditor from './HeroEditor';
import { ServicesEditor, TestimonialsEditor, VideoEditor } from './ItemsEditors';
import ArticlesEditor from './ArticlesEditor';
import GalleryEditor from './GalleryEditor';
import OfficeEditor from './OfficeEditor';
import BookingEditor from './BookingEditor';

// ─────────────────────────────────────────────────────────────────────────────
// One section's editor, whichever kind it is: what stops it from being
// published (the service's blockers and the last «انتشار» refusal land HERE,
// on the owning card), whether the current template shows it at all, the
// section heading override, then the kind's own editor.
// Surface-neutral: the desktop wraps it in a card, the phone in a full-screen
// content screen.
// ─────────────────────────────────────────────────────────────────────────────

const EDITORS = {
  hero: HeroEditor,
  services: ServicesEditor,
  articles: ArticlesEditor,
  testimonials: TestimonialsEditor,
  gallery: GalleryEditor,
  video: VideoEditor,
  office: OfficeEditor,
  booking: BookingEditor,
};

function TitleOverride({ section, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const { draft, setDraft, dirty, adopt, reset } = useDraft({ titleOverride: section.titleOverride || '' });
  const key = `title-${section.kind}`;
  const card = cards[key] || {};
  const save = async () => {
    const res = await actions.updateSection(key, section.kind, { titleOverride: draft.titleOverride.trim() });
    const saved = res.ok ? res.page?.sections?.find((x) => x.kind === section.kind) : null;
    if (saved) adopt({ titleOverride: saved.titleOverride || '' });
  };
  return (
    <div style={{ display: 'grid', gap: '0.5rem' }}>
      <TextInput
        label={s('myPage.sections.titleOverride')}
        placeholder={sectionMeta(section.kind).title}
        helperText={s('myPage.sections.titleOverrideHint')}
        value={draft.titleOverride}
        onChange={(e) => setDraft({ titleOverride: e.target.value })}
        maxLength={LIMITS.title_override_max}
        disabled={readOnly}
        inputStyle={fieldStyle}
        data-testid={`my-page-${section.kind}-title-override`}
      />
      {dirty || card.saving || card.error ? (
        <SaveRow
          status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
          error={card.error}
          onSave={save}
          onReset={reset}
          disabled={readOnly}
          testId={`my-page-${section.kind}-title`}
        />
      ) : null}
    </div>
  );
}

export default function SectionEditor({ page, kind, actions, cards, readOnly, publishErrors }) {
  const s = useMyPageStrings();
  const section = (page.sections || []).find((x) => x.kind === kind);
  const Editor = EDITORS[kind];
  if (!section || !Editor) return null;

  const toggleCard = cards[`toggle-${kind}`] || {};
  const pageErrors = errorsBySection(page.publishBlockers)[kind] || [];
  const lastRefusal = errorsBySection(publishErrors)[kind] || [];
  const allErrors = [...lastRefusal, ...pageErrors.filter((e) => !lastRefusal.some((r) => r.code === e.code && r.field === e.field))];
  // A kind whose editor shows some codes on their own fields (booking) keeps
  // only the rest in the card's list — one error, one place.
  const { fields, rest } = kind === 'booking' ? splitBookingFieldErrors(allErrors) : { fields: null, rest: allErrors };
  const fieldErrors = fields ? [...fields.clinic, ...fields.services] : [];

  return (
    <div style={{ display: 'grid', gap: '1rem' }} data-testid={`my-page-editor-${kind}`}>
      {!section.supportedByTemplate ? (
        <Alert variant="neutral" icon={<CircleSlash size={16} aria-hidden />} data-testid={`my-page-editor-${kind}-unsupported`}>
          {s('myPage.sections.unsupportedLong')}
        </Alert>
      ) : null}
      {!section.enabled && !section.required && !Editor.ownsEnable ? (
        // A hidden section says so in words and is switched on right here —
        // a bare «پنهان» badge read as a broken card, not as a choice.
        <Alert
          variant="neutral"
          icon={<EyeOff size={16} aria-hidden />}
          data-testid={`my-page-editor-${kind}-hidden`}
          action={(
            <Button
              size="sm"
              disabled={readOnly || !!toggleCard.saving}
              onClick={() => actions.updateSection(`toggle-${kind}`, kind, { enabled: true })}
              data-testid={`my-page-editor-${kind}-show`}
            >
              {s('myPage.sections.showOnPage')}
            </Button>
          )}
        >
          {s('myPage.sections.hiddenNotice')}
        </Alert>
      ) : null}
      <ErrorLine error={toggleCard.error} testId={`my-page-editor-${kind}-toggle-error`} />
      <PublishErrorList errors={rest} testId={`my-page-editor-${kind}-errors`} />
      {kind !== 'hero' ? <TitleOverride section={section} actions={actions} cards={cards} readOnly={readOnly} /> : null}
      <Editor page={page} section={section} actions={actions} cards={cards} readOnly={readOnly} fieldErrors={fieldErrors} />
    </div>
  );
}
